import { HttpRequest } from "./utils/HttpRequest";
import { Mrz } from "./models/Mrz";
import { dataURLtoFile } from "../utils/imageUtil";
import { LIMIT_PEOPLEINSIDE } from "../utils/constants/tableConstants";
import { VehicleFileType } from "./vehicle";
import {
  AuthorizationResponse,
  FindResidentsResponse,
  PeopleInsideResponse,
  PrevalidationsResponse,
  ProviderAuthorizationResponse,
  UserResponse,
  VehiclesInsideResponse,
  UpdateEventDockResponse,
} from "./types/userTypes";

export default class User {
  httpReq = new HttpRequest();
  //Services
  findUserByDNI(token: string, dni: string, countryCode: string): Promise<UserResponse> {
    return this.httpReq.post(`ws-security/findUser-DNI`, {
      token: token,
      dni: dni,
      countryCode: countryCode,
    });
  }

  findUserByMRZ(token: string, mrz: Mrz): Promise<UserResponse> {
    return this.httpReq.post(`ws-security/findUser-MRZ`, {
      token,
      mrz,
    });
  }

  async findUserByLPQR(token: string, qr: string): Promise<any> {
    return await this.httpReq.post(`ws-security/getUser`, {
      token,
      qr,
    });
  }

  getAuthorizations(
    token: string,
    dni: string,
    countryCode: string
  ): Promise<AuthorizationResponse> {
    return this.httpReq.post(`ws-security/getAuthorizations`, {
      token,
      dni,
      countryCode,
      showPendingRequests: "true",
    });
  }

  async getPrevalidations(
    token: string,
    type: string,
    dni: string,
    countryCode: string
  ): Promise<PrevalidationsResponse> {
    return await this.httpReq.post(`ws-security/getPrevalidations`, {
      token,
      type,
      dni,
      countryCode,
    });
  }

  /** Aseguradoras de riesgos del trabajo (ART) dadas de alta por el admin. */
  getArtCompanies(token: string, search?: string): Promise<any> {
    const body: { token: string; search?: string } = { token };
    if (search) {
      body.search = search;
    }
    return this.httpReq.post(`ws-security/getArtCompanies`, body);
  }

  async getProvidersAuthorizations(
    token: string,
    host: string,
    lot: string
  ): Promise<ProviderAuthorizationResponse> {
    return await this.httpReq.post(`ws-security/getProviderAuthorizations`, {
      token,
      host,
      lot,
    });
  }

  getLastEntrance(
    token: string,
    id: string,
    dni: string,
    countryCode: string,
    ignoreExit: boolean
  ) {
    return this.httpReq.post(`ws-security/getLastEntrance`, {
      token,
      id,
      dni,
      countryCode,
      ignoreExit,
    });
  }

  getPendingRequestsData(token: string) {
    return this.httpReq.post(`ws-security/getPendingRequests`, {
      token,
    });
  }

  getPendingRequestsCount(token: string, counter: boolean) {
    return this.httpReq.post(`ws-security/getPendingRequests`, {
      token,
      counter,
    });
  }

  uploadImgEvent(token: string, file: string, id: string, fileType: string, fileName: string) {
    let formData = new FormData();
    formData.append("token", token);
    formData.append("file", dataURLtoFile(file, fileName));
    formData.append("fileType", fileType);
    formData.append("eventId", id);
    return this.httpReq.post(
      `ws-security/upload`,
      {
        formData,
      },
      "none"
    );
  }

  async uploadImgUser(token: string, file: string, id: string, fileType: string, fileName: string) {
    let formData = new FormData();
    formData.append("token", token);
    formData.append("file", dataURLtoFile(file, fileName));
    formData.append("fileType", fileType);
    formData.append("id", id);
    return await this.httpReq.post(
      `ws-security/uploadUser`,
      {
        formData,
      },
      "none"
    );
  }

  async uploadImgVehicle(
    token: string,
    file: string,
    id: string,
    fileType: VehicleFileType,
    fileName: string
  ) {
    let formData = new FormData();
    formData.append("token", token);
    formData.append("file", dataURLtoFile(file, fileName));
    formData.append("fileType", fileType);
    formData.append("id", id);
    return await this.httpReq.post(
      `ws-security/uploadVehicle`,
      {
        formData,
      },
      "none"
    );
  }

  getImagesByTokenId(token: string, id: string) {
    return this.httpReq.get(`ws-security/images/${id}?token=${token}`, null);
  }

  getTokenAccessImage(token: string) {
    return this.httpReq.post(`ws-security/getFileAccessToken`, {
      token,
    });
  }

  updatePendingRequest(token: string, id: string, action: string) {
    return this.httpReq.post(`ws-security/get`, {
      token,
      id,
      action,
    });
  }

  previewInvite(token: string, id: string) {
    return this.httpReq.post(`ws-security/previewInvite`, {
      token,
      id,
    });
  }

  async updateUser(token: string, id: string, field: any) {
    return await this.httpReq.post(`ws-security/updateUser`, {
      token,
      id,
      ...field,
    });
  }

  findResidents(token: string, q: string): Promise<FindResidentsResponse> {
    return this.httpReq.get(`ws-security/findResidents?json={"token":"${token}","q":"${q}"}`, null);
  }

  findEmployees(token: string, q: string): Promise<any> {
    return this.httpReq.get(`ws-security/findEmployees?json={"token":"${token}","q":"${q}"}`, null);
  }

  getPeopleInside(
    token: string,
    beginTimestamp: number,
    endTimestamp: number,
    page?: number,
    limit?: number,
    search?: string,
    sortBy?: string,
    sortDirection?: "asc" | "desc",
    companies?: string[],
    authorizationTags?: string[],
    transportCompanies?: string[]
  ): Promise<PeopleInsideResponse> {
    return this.httpReq.post(`ws-security/getPeopleInside`, {
      token,
      beginTimestamp,
      endTimestamp,
      page: page ?? 0,
      limit: limit ?? LIMIT_PEOPLEINSIDE,
      search: search ?? "",
      origin: "web",
      sortBy: "datetime",
      sortDirection: sortDirection ?? "desc",
      ...(companies && companies.length ? { companies } : {}),
      ...(authorizationTags && authorizationTags.length ? { authorizationTags } : {}),
      ...(transportCompanies && transportCompanies.length ? { transportCompanies } : {}),
    });
  }

  /** Actualiza el dock de un evento de acceso de transporte que sigue dentro del complejo. */
  updateEventDock(token: string, event: string, dock: string): Promise<UpdateEventDockResponse> {
    return this.httpReq.post(`ws-security/transport/updateEventDock`, {
      token,
      event,
      dock,
    });
  }

  /**
   * Unidades dentro del complejo: una fila por vehículo, con su rol, el estado del chofer y las
   * unidades asociadas. A diferencia de getPeopleInside no lleva ventana de tiempo ni
   * `authorizationTags`: el endpoint no los acepta.
   */
  getVehiclesInside(
    token: string,
    page?: number,
    limit?: number,
    search?: string,
    sortDirection?: "asc" | "desc",
    companies?: string[],
    transportCompanies?: string[]
  ): Promise<VehiclesInsideResponse> {
    return this.httpReq.post(`ws-security/getVehiclesInside`, {
      token,
      page: page ?? 0,
      limit: limit ?? LIMIT_PEOPLEINSIDE,
      search: search ?? "",
      origin: "web",
      sortDirection: sortDirection ?? "desc",
      ...(companies && companies.length ? { companies } : {}),
      ...(transportCompanies && transportCompanies.length ? { transportCompanies } : {}),
    });
  }
}
